<template>
  <div>
    <!-- Filtros -->
    <report-filters
      :selectedLegalEntity.sync="selectedLegalEntity"
      :startDate.sync="startDate"
      :endDate.sync="endDate"
      :loading="loading"
      :hasData="reportData && reportData.length > 0"
      reportType="type_reporting"
      @search="loadReportData"
      @clear="clearFilters"
      @export="exportToPdf"
    />

    <!-- Tabela de Resultados -->
    <b-row>
      <b-col cols="12">
        <b-card>
          <b-card-title>{{
            $t('reporting_channel.reports.type_reporting.title')
          }}</b-card-title>
          <b-card-body>
            <b-alert
              v-if="!reportData || reportData.length === 0"
              show
              variant="info"
            >
              {{
                $t('reporting_channel.reports.type_reporting.no_data_message')
              }}
            </b-alert>
            <b-table
              v-else
              :items="reportData"
              :fields="tableFields"
              striped
              hover
              responsive
              small
              class="mb-0"
            >
              <template #cell(type_reporting)="row">
                {{ row.item.type_reporting.name || '-' }}
              </template>
              <template #cell(total)="row">
                <strong class="text-primary">{{ row.item.total }}</strong>
              </template>
              <template #cell(percentual)="row">
                <strong class="text-success">{{
                  formatPercentual(row.item.percentual)
                }}</strong>
              </template>
            </b-table>
            <div
              v-if="reportData && reportData.length > 0"
              class="mt-3 text-right"
            >
              <strong class="text-primary">
                {{
                  $t('reporting_channel.reports.type_reporting.total_geral')
                }}:
                {{ totalGeral }}
              </strong>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BAlert,
  BTable
} from 'bootstrap-vue'
import ReportFilters from '@/components/report/ReportFilters.vue'
import ReportingChannelFormHandlingService from '@/services/api/reporting-channel/ReportingChannelFormHandlingService'
import { isAdmin } from '@/util/Util'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BAlert,
    BTable,
    ReportFilters
  },
  data() {
    // Calcular datas dos últimos 30 dias
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 30)

    return {
      service: null,
      selectedLegalEntity: null,
      startDate: startDate,
      endDate: endDate,
      reportData: null,
      totalGeral: 0,
      loading: false
    }
  },
  computed: {
    tableFields() {
      return [
        {
          key: 'type_reporting',
          label: this.$t(
            'reporting_channel.reports.type_reporting.table.type_reporting'
          ),
          sortable: true
        },
        {
          key: 'total',
          label: this.$t(
            'reporting_channel.reports.type_reporting.table.total'
          ),
          sortable: true
        },
        {
          key: 'percentual',
          label: this.$t(
            'reporting_channel.reports.type_reporting.table.percentual'
          ),
          sortable: true
        }
      ]
    }
  },
  methods: {
    legalEntityItemTextFormat(model) {
      let name = `${model.company_name} - ${model.trading_name}`
      const hasCnpj = !!model.cnpj
      if (hasCnpj) {
        name = `${name} ( ${this.cnpjFormat(model.cnpj)}) `
      }
      return name
    },
    loadReportData() {
      this.loading = true
      this.$vs.loading()

      const params = {}
      if (this.selectedLegalEntity && this.selectedLegalEntity.id) {
        params.legal_entity_id = this.selectedLegalEntity.id
      }
      if (this.startDate) {
        params.start_date = this.formatDateForApi(this.startDate)
      }
      if (this.endDate) {
        params.end_date = this.formatDateForApi(this.endDate)
      }

      this.service
        .getTypeReportingReport(params)
        .then(
          data => {
            this.reportData = data.data || []
            this.totalGeral = data.total_geral || 0
            this.$vs.loading.close()
            this.loading = false
          },
          error => {
            this.$vs.loading.close()
            this.loading = false
            this.notifyError(
              this.$vs,
              'Não foi possível carregar os dados do relatório'
            )
          }
        )
        .finally(() => {
          this.$vs.loading.close()
          this.loading = false
        })
    },
    formatDateForApi(date) {
      if (!date) return null
      if (typeof date === 'string') {
        if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return date
        }
        const parts = date.split('/')
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`
        }
      }
      if (date instanceof Date) {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      return null
    },
    clearFilters() {
      this.selectedLegalEntity = null
      // Resetar para os últimos 30 dias
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - 30)
      this.startDate = startDate
      this.endDate = endDate
      this.reportData = null
      this.totalGeral = 0
    },
    formatPercentual(value) {
      if (value === null || value === undefined) return '0%'
      return `${value.toFixed(2)}%`
    },
    exportToPdf() {
      if (!this.reportData || this.reportData.length === 0) {
        this.$vs.notify({
          title: this.$t('messages.error'),
          text: 'Não há dados para exportar',
          color: 'warning',
          icon: 'AlertCircleIcon'
        })
        return
      }

      this.$vs.loading()

      const params = {}
      if (this.selectedLegalEntity && this.selectedLegalEntity.id) {
        params.legal_entity_id = this.selectedLegalEntity.id
      }
      if (this.startDate) {
        params.start_date = this.formatDateForApi(this.startDate)
      }
      if (this.endDate) {
        params.end_date = this.formatDateForApi(this.endDate)
      }

      this.service
        .exportTypeReportingReportPdf(params)
        .then(() => {
          this.$vs.loading.close()
          this.$vs.notify({
            title: this.$t('messages.success_title'),
            text: 'Relatório PDF gerado com sucesso',
            color: 'success',
            icon: 'CheckIcon'
          })
        })
        .catch(error => {
          this.$vs.loading.close()
          this.notifyError(this.$vs, 'Não foi possível gerar o relatório PDF')
        })
    }
  },
  beforeMount() {
    this.service = ReportingChannelFormHandlingService.build(this.$vs)
  },
  mounted() {
    // Carregar dados automaticamente ao montar o componente
    this.loadReportData()
  }
}
</script>

<style scoped></style>
